import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, CrosshairMode } from 'lightweight-charts';
import { formatPrice, parseFloatFromString } from '../utils/formatters';
import { useTheme } from './ThemeProvider';
import { TransactionType } from '../utils/enums';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { findTransactionMarkerDataPoint, normalizeTimeValue } from '../utils/chartMarkers';
import { getRiskScoreChartData, getRiskScoreColor } from '../utils/riskScoreColors';

// Standard themes (Solarized Light)
const LIGHT_THEME = {
  background: '#fdf6e3', // base3
  text: '#657b83',       // base00
  grid: 'rgba(147, 161, 161, 0.3)', // base1 at 30%
  crosshair: 'rgba(88, 110, 117, 0.4)', // base01 at 40%
  border: 'rgba(238, 232, 213, 0.9)', // base2 at 90%
  watermark: 'rgba(101, 123, 131, 0.15)', // base00 at 15%
  price: {
    line: '#268bd2', // blue
    area: {
      top: 'rgba(38, 139, 210, 0.25)',
      bottom: 'rgba(38, 139, 210, 0.06)',
    },
    tooltip: {
      background: '#fffdf5', // softer light surface
      border: '#268bd2',
      text: '#586e75',
    }
  },
  avgBuyPrice: '#b58900', // yellow
  avgSellPrice: '#dc322f', // red
  transactions: {
    buy: '#2aa198', // cyan/green
    sell: '#cb4b16' // orange
  },
  exitLevels: {
    default: '#A5D6A7', // light green planned
    orderSet: '#268bd2', // blue
    filled: '#859900' // green
  },
  riskScore: '#00897b' // teal 600
};

// Gruvbox dark theme (commented out)
/*
const GRUVBOX_DARK_THEME = {
  background: '#282828',
  text: '#ebdbb2',
  grid: 'rgba(60, 56, 54, 0.6)',
  crosshair: 'rgba(189, 174, 147, 0.4)',
  border: 'rgba(80, 73, 69, 0.8)',
  watermark: 'rgba(235, 219, 178, 0.12)',
  price: {
    line: '#4CAF50', // Keeping the green color as requested
    area: {
      top: 'rgba(76, 175, 80, 0.4)', // Keeping green area
      bottom: 'rgba(76, 175, 80, 0.05)',
    },
    tooltip: {
      background: '#3c3836',
      border: '#8ec07c',
      text: '#fbf1c7',
    }
  },
  avgBuyPrice: '#fabd2f', // Gruvbox yellow
  avgSellPrice: '#ff5f5f', // Gruvbox red
  transactions: {
    buy: '#83a598', // Gruvbox blue
    sell: '#d3869b'  // Gruvbox purple
  },
  exitLevels: {
    default: '#fe8019', // Gruvbox orange
    orderSet: '#458588', // Gruvbox blue
    filled: '#b8bb26'  // Gruvbox green
  }
};
*/

// Material UI dark theme
const DARK_THEME = {
  background: '#121212', // Material UI dark surface
  text: '#ffffff',
  grid: 'rgba(255, 255, 255, 0.12)', // Material UI divider
  crosshair: 'rgba(255, 255, 255, 0.38)',
  border: 'rgba(255, 255, 255, 0.23)',
  watermark: 'rgba(255, 255, 255, 0.08)',
  price: {
    line: '#2196f3', // Material UI blue
    area: {
      top: 'rgba(33, 150, 243, 0.3)',
      bottom: 'rgba(33, 150, 243, 0.06)',
    },
    tooltip: {
      background: '#1e1e1e', // Darker surface
      border: '#2196f3',
      text: '#ffffff',
    }
  },
  avgBuyPrice: '#ffb74d', // Material UI orange
  avgSellPrice: '#f06292', // Material UI pink
  transactions: {
    buy: '#00e676', // Material UI Green A400
    sell: '#ff5252'  // Material UI Red A200
  },
  exitLevels: {
    default: '#A5D6A7', // Light green for planned levels
    orderSet: '#2196f3', // Material UI blue
    filled: '#4caf50'  // Material UI green
  },
  riskScore: '#26A69A' // Material teal 400
};

const compactCurrencySymbol = (currency) => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0).find(part => part.type === 'currency')?.value || '$';
  } catch (_) {
    return '$';
  }
};

const formatChartPriceLabel = (price, currency) => {
  const num = typeof price === 'string' ? parseFloatFromString(price) : price;
  if (num === null || num === undefined || Number.isNaN(num)) return formatPrice(price, currency);
  const absVal = Math.abs(num);
  if (absVal > 0 && absVal < 0.000001) {
    return `${compactCurrencySymbol(currency)}${num.toExponential(2)}`;
  }
  return formatPrice(num, currency);
};

const PriceChart = ({ 
  data = [], 
  width = '100%', 
  height = '400px',
  averageBuyPrice,
  averageSellPrice,
  transactions = [],
  plannedLevels = [],
  holdingsAmount = 0,
  ticker = '',
  riskScoreHistory = []
}) => {
  const { darkMode } = useTheme();
  const { currentPortfolio } = usePortfolioContext();
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const lineSeriesRef = useRef(null);
  const areaSeriesRef = useRef(null);
  const avgPriceLineRef = useRef(null);
  const avgSellPriceLineRef = useRef(null);
  const exitLevelLinesRef = useRef([]);
  const riskScoreSeriesRef = useRef(null);
  const chartCreatedRef = useRef(false);
  const toolTipRef = useRef(null);
  const [error, setError] = useState(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  const prevPlannedLevelsRef = useRef([]); // Reference to store previous planned levels
  
  // Select theme based on current app theme
  const CHART_THEME = darkMode ? DARK_THEME : LIGHT_THEME;
  
  // Format real transactions as chart markers
  const formatTransactionsAsMarkers = useCallback((transactions, chartData) => {
    if (!chartData || chartData.length === 0 || !transactions || transactions.length === 0) {
      return [];
    }
    
    // Filter transactions to just buys and sells
    const validTransactions = transactions.filter(tx => {
      // Normalize transaction type to always use uppercase enum format
      const txType = tx.transactionType?.toUpperCase?.() || tx.transactionType;
      return txType === TransactionType.BUY || txType === TransactionType.SELL;
    });
    
    if (validTransactions.length === 0) {
      return [];
    }
    
    const markers = validTransactions.map(tx => {
      const dataPoint = findTransactionMarkerDataPoint(chartData, tx.timestamp);
      
      if (!dataPoint) {
        return null; // Skip transactions without timestamp or chart data
      }
      
      // Normalize transaction type to always use uppercase enum format
      const txType = tx.transactionType?.toUpperCase?.() || tx.transactionType;
      const isBuy = txType === TransactionType.BUY;
      
      // Parse price and quantity. Prefer scaledValues.price (portfolio currency) when available.
      const scaledPriceStr = tx?.scaledValues?.price;
      const price = parseFloatFromString((scaledPriceStr && String(scaledPriceStr)) || tx.price);
      const quantity = parseFloatFromString(tx.quantity);
      
      // Calculate total value (price × quantity)
      const totalValue = price * quantity;
      const trimTrailingZeros = (str) => str.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
      const formattedTotal = trimTrailingZeros(formatPrice(totalValue, currentPortfolio?.currency));
      
      // Format unit price without currency symbol for cleaner display
      const formattedUnitPrice = trimTrailingZeros(formatChartPriceLabel(price, currentPortfolio?.currency).replace(/^[^0-9-]+/, ''));
      
      // Create marker with transaction details showing total value @ unit price
      return {
        time: dataPoint.time,
        position: isBuy ? 'belowBar' : 'aboveBar',
        color: isBuy ? CHART_THEME.transactions.buy : CHART_THEME.transactions.sell,
        shape: 'circle',
        text: `${formattedTotal} @ ${formattedUnitPrice}`,
        size: 1.5
      };
    }).filter(Boolean);
    
    return markers;
  }, [CHART_THEME.transactions.buy, CHART_THEME.transactions.sell, currentPortfolio?.currency]);
  
  // Update chart data function
  const updateChartData = useCallback((chartData, buyPrice, transactions = [], exitLevels = []) => {
    if (!chartRef.current || !chartCreatedRef.current || !chartData || chartData.length === 0 || 
        !lineSeriesRef.current || !areaSeriesRef.current) {
      return;
    }
    
    try {
      // Format the data if needed (if not already formatted)
      let formattedData = chartData;
      if (chartData[0] && (chartData[0].price !== undefined || chartData[0].value === undefined)) {
        formattedData = formatChartData(chartData);
      }
      
      if (formattedData.length === 0) {
        setError("Invalid price data format");
        return;
      }
      
      // Store formatted data for tooltip access
      if (chartContainerRef.current) {
        chartContainerRef.current.dataset.formattedData = JSON.stringify(formattedData);
        chartContainerRef.current.dataset.ticker = ticker;
      }
      
      // Update price formatter based on current price range
      chartRef.current.applyOptions({
        localization: {
          priceFormatter: price => {
            return formatChartPriceLabel(price, currentPortfolio?.currency);
          },
        },
      });
      
      // Update data for existing series
      const lineSeries = lineSeriesRef.current;
      const areaSeries = areaSeriesRef.current;
      
      // Set price data to both series
      lineSeries.setData(formattedData);
      areaSeries.setData(formattedData);
      
      // Remove the existing average buy price line if it exists
      if (avgPriceLineRef.current) {
        try {
          lineSeries.removePriceLine(avgPriceLineRef.current);
          avgPriceLineRef.current = null;
        } catch (err) {
          // Silent fail
          avgPriceLineRef.current = null;
        }
      }
      
      // Remove the existing average sell price line if it exists
      if (avgSellPriceLineRef.current) {
        try {
          lineSeries.removePriceLine(avgSellPriceLineRef.current);
          avgSellPriceLineRef.current = null;
        } catch (err) {
          // Silent fail
          avgSellPriceLineRef.current = null;
        }
      }
      
      // Add average buy price line if needed
      if (buyPrice !== undefined && buyPrice !== null) {
        const parsedBuyPrice = parseFloatFromString(buyPrice);
        // Only create price line if the parsed price is valid and greater than 0
        if (parsedBuyPrice > 0) {
          try {
            // Create price line
            const priceLine = {
              price: parsedBuyPrice,
              color: CHART_THEME.avgBuyPrice,
              lineWidth: 1,
              lineStyle: 1, // Dashed
              axisLabelVisible: true,
              title: 'Avg Buy',
            };
            
            // Store the reference to the price line
            avgPriceLineRef.current = lineSeries.createPriceLine(priceLine);
          } catch (err) {
            // Silent fail
          }
        }
      }
      
      // Add average sell price line if needed and enabled
      if (averageSellPrice !== undefined && averageSellPrice !== null) {
        const parsedSellPrice = parseFloatFromString(averageSellPrice);
        // Only create price line if the parsed price is valid and greater than 0
        if (parsedSellPrice > 0) {
          try {
            // Create price line
            const priceLine = {
              price: parsedSellPrice,
              color: CHART_THEME.avgSellPrice,
              lineWidth: 1,
              lineStyle: 1, // Dashed
              axisLabelVisible: true,
              title: 'Avg Sell',
            };
            
            // Store the reference to the price line
            avgSellPriceLineRef.current = lineSeries.createPriceLine(priceLine);
          } catch (err) {
            // Silent fail
          }
        }
      }
      
      // Remove any existing exit level price lines
      if (exitLevelLinesRef.current.length > 0) {
        exitLevelLinesRef.current.forEach(line => {
          try {
            lineSeries.removePriceLine(line);
          } catch (err) {
            // Silent fail
          }
        });
        exitLevelLinesRef.current = [];
      }
      
      // Add exit level price lines
      if (exitLevels && exitLevels.length > 0) {
        // Calculate total holdings
        const totalHoldings = holdingsAmount > 0 ? 
          parseFloatFromString(holdingsAmount) : 
          exitLevels.reduce((sum, level) => {
            const amount = parseFloatFromString(level.amount || 0);
            return sum + amount;
          }, 0);
        
        exitLevels.forEach((level, index) => {
          // Skip invalid levels
          if (!level || typeof level !== 'object') {
            console.warn('Invalid exit level format:', level);
            return;
          }
            
          const levelPrice = parseFloatFromString(level.price);
          if (levelPrice > 0) {
            try {
              // Calculate percentage from amount if available
              let levelPercentage = parseFloatFromString(level.percentage); // Try to use existing percentage first
              
              // If no percentage but we have amount and total holdings, calculate percentage
              if ((!levelPercentage || levelPercentage === 0) && totalHoldings > 0) {
                const amount = parseFloatFromString(level.amount || 0);
                if (amount > 0) {
                  levelPercentage = (amount / totalHoldings) * 100;
                }
              }
              
              // Default percentage if still not available
              if (!levelPercentage || levelPercentage === 0) {
                levelPercentage = 100 / exitLevels.length; // Equal distribution as fallback
              }
              
              // Get status color
              let statusColor = CHART_THEME.exitLevels.default; // Default orange
              if (level.status === 'ORDER_SET') {
                statusColor = CHART_THEME.exitLevels.orderSet; // Blue for orders set
              } else if (level.status === 'FILLED') {
                statusColor = CHART_THEME.exitLevels.filled; // Light green for filled
              } else {
                statusColor = CHART_THEME.exitLevels.default; // Orange for planned levels
              }
              
              // Get the amount value and format it
              const amount = parseFloatFromString(level.amount || 0);
              const formattedAmount = amount.toLocaleString(undefined, { 
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
              });
              
              // Create price line for this exit level
              const displayLevelNumber = Number.isInteger(level.chartLevelNumber)
                ? level.chartLevelNumber
                : index + 1;

              const priceLine = {
                price: levelPrice,
                color: statusColor,
                lineWidth: 1,
                lineStyle: 2, // Dashed
                axisLabelVisible: true,
                title: `Exit L${displayLevelNumber}: ${formattedAmount} (${levelPercentage.toFixed(1)}%)`,
              };
              
              // Create and store the price line reference
              const line = lineSeries.createPriceLine(priceLine);
              exitLevelLinesRef.current.push(line);
            } catch (err) {
              // Silent fail
            }
          }
        });
      }
      
      // Clear any existing markers first
      lineSeries.setMarkers([]);
      
      // Generate markers from real transactions if available
      let markers = [];
      if (transactions && transactions.length > 0) {
        // Format markers and add them
        markers = formatTransactionsAsMarkers(transactions, formattedData);
        
        if (markers && markers.length > 0) {
          try {
            // Ensure marker visibility
            lineSeries.applyOptions({
              markers: {
                visible: true,
                attachToSeries: true,
                size: 2
              }
            });
            
            // Apply markers to chart
            lineSeries.setMarkers(markers);
          } catch (err) {
            // Silent fail
          }
        }
      }
      
      // Fit the chart content
      chartRef.current.timeScale().fitContent();
    } catch (err) {
      setError(`Chart update error: ${err.message}`);
    }
  }, [
    CHART_THEME.avgBuyPrice,
    CHART_THEME.avgSellPrice,
    CHART_THEME.exitLevels.default,
    CHART_THEME.exitLevels.orderSet,
    CHART_THEME.exitLevels.filled,
    averageSellPrice,
    currentPortfolio?.currency,
    ticker,
    holdingsAmount,
    formatTransactionsAsMarkers
  ]);
  
  // Format data to lightweight-charts format
  const formatChartData = (inputData) => {
    if (!inputData || inputData.length === 0) return [];
    
    return inputData.map(point => ({
      time: point.time,
      value: typeof point.price !== 'undefined' ? point.price : point.value
    }));
  };
  
  // Clean up function to remove chart
  const cleanUpChart = () => {
    if (chartRef.current) {
      if (avgPriceLineRef.current && lineSeriesRef.current) {
        try {
          lineSeriesRef.current.removePriceLine(avgPriceLineRef.current);
        } catch (err) {
          // Silent fail
        }
      }
      
      if (avgSellPriceLineRef.current && lineSeriesRef.current) {
        try {
          lineSeriesRef.current.removePriceLine(avgSellPriceLineRef.current);
        } catch (err) {
          // Silent fail
        }
      }
      
      chartRef.current.remove();
      chartRef.current = null;
      lineSeriesRef.current = null;
      areaSeriesRef.current = null;
      avgPriceLineRef.current = null;
      avgSellPriceLineRef.current = null;
      exitLevelLinesRef.current = [];
      riskScoreSeriesRef.current = null;
      chartCreatedRef.current = false;
      
      if (toolTipRef.current && toolTipRef.current.parentNode) {
        toolTipRef.current.parentNode.removeChild(toolTipRef.current);
        toolTipRef.current = null;
      }
    }
  };
  
  // Clean up chart on unmount
  useEffect(() => {
    return () => {
      cleanUpChart();
    };
  }, []);
  
  // Update chart when theme changes
  useEffect(() => {
    cleanUpChart();
    setForceUpdate(prev => prev + 1);
  }, [darkMode]);
  
  // Update chart when transaction data changes
  useEffect(() => {
    if (!chartCreatedRef.current || !lineSeriesRef.current) {
      return;
    }
    
    if (data.length > 0) {
      // Force chart refresh on transaction changes to update markers and average buy price
      cleanUpChart();
      setForceUpdate(prev => prev + 1);
    }
  }, [transactions, data.length]);
  
  // Update chart when exit levels change
  useEffect(() => {
    // Skip if no chart is created yet
    if (!chartCreatedRef.current || !lineSeriesRef.current) {
      return;
    }
    
    // Only update if planned levels have actually changed
    const prevLevelsJSON = JSON.stringify(prevPlannedLevelsRef.current);
    const currentLevelsJSON = JSON.stringify(plannedLevels);
    
    if (prevLevelsJSON !== currentLevelsJSON) {
      // Update the previous levels reference
      prevPlannedLevelsRef.current = plannedLevels;
      
      // Remove any existing exit level price lines
      if (exitLevelLinesRef.current.length > 0) {
        exitLevelLinesRef.current.forEach(line => {
          try {
            lineSeriesRef.current.removePriceLine(line);
          } catch (err) {
            // Silent fail
          }
        });
        exitLevelLinesRef.current = [];
      }
      
      // Add exit level price lines with current data
      if (data.length > 0) {
        const formattedData = formatChartData(data);
        updateChartData(formattedData, averageBuyPrice, transactions, plannedLevels);
      }
    }
  }, [plannedLevels, holdingsAmount, data, averageBuyPrice, transactions, updateChartData]);
  
  // Update average buy price line when it changes
  useEffect(() => {
    if (chartCreatedRef.current && lineSeriesRef.current && data.length > 0) {
      // Remove the existing average buy price line if it exists
      if (avgPriceLineRef.current) {
        try {
          lineSeriesRef.current.removePriceLine(avgPriceLineRef.current);
          avgPriceLineRef.current = null;
        } catch (err) {
          // Silent fail
          avgPriceLineRef.current = null;
        }
      }
      
      // Add average buy price line if needed
      if (averageBuyPrice !== undefined && averageBuyPrice !== null) {
        const parsedBuyPrice = parseFloatFromString(averageBuyPrice);
        // Only create price line if the parsed price is valid and greater than 0
        if (parsedBuyPrice > 0) {
          try {
            // Create price line
            const priceLine = {
              price: parsedBuyPrice,
              color: CHART_THEME.avgBuyPrice,
              lineWidth: 1,
              lineStyle: 1, // Dashed
              axisLabelVisible: true,
              title: 'Avg Buy',
            };
            
            // Store the reference to the price line
            avgPriceLineRef.current = lineSeriesRef.current.createPriceLine(priceLine);
          } catch (err) {
            console.warn('Error updating average buy price line:', err);
          }
        }
      }
    }
  }, [averageBuyPrice, data, CHART_THEME.avgBuyPrice]);
  
  // Update average sell price line when it changes or toggle state changes
  useEffect(() => {
    if (chartCreatedRef.current && lineSeriesRef.current && data.length > 0) {
      // Remove the existing average sell price line if it exists
      if (avgSellPriceLineRef.current) {
        try {
          lineSeriesRef.current.removePriceLine(avgSellPriceLineRef.current);
          avgSellPriceLineRef.current = null;
        } catch (err) {
          // Silent fail
          avgSellPriceLineRef.current = null;
        }
      }
      
      // Add average sell price line if needed and enabled
      if (averageSellPrice !== undefined && averageSellPrice !== null) {
        const parsedSellPrice = parseFloatFromString(averageSellPrice);
        // Only create price line if the parsed price is valid and greater than 0
        if (parsedSellPrice > 0) {
          try {
            // Create price line
            const priceLine = {
              price: parsedSellPrice,
              color: CHART_THEME.avgSellPrice,
              lineWidth: 1,
              lineStyle: 1, // Dashed
              axisLabelVisible: true,
              title: 'Avg Sell',
            };
            
            // Store the reference to the price line
            avgSellPriceLineRef.current = lineSeriesRef.current.createPriceLine(priceLine);
          } catch (err) {
            console.warn('Error updating average sell price line:', err);
          }
        }
      }
    }
  }, [averageSellPrice, data, CHART_THEME.avgSellPrice]);
  
  // Add a specific effect to update transaction markers when transactions change
  useEffect(() => {
    if (chartCreatedRef.current && lineSeriesRef.current && data.length > 0) {
      const formattedData = formatChartData(data);
      
      // Clear any existing markers first
      lineSeriesRef.current.setMarkers([]);
      
      // Generate markers from real transactions if available
      if (transactions && transactions.length > 0) {
        // Format markers and add them
        const markers = formatTransactionsAsMarkers(transactions, formattedData);
        
        if (markers && markers.length > 0) {
          try {
            // Ensure marker visibility
            lineSeriesRef.current.applyOptions({
              markers: {
                visible: true,
                attachToSeries: true,
                size: 2
              }
            });
            
            // Apply markers to chart
            lineSeriesRef.current.setMarkers(markers);
          } catch (err) {
            console.warn('Error setting transaction markers:', err);
          }
        }
      }
    }
  }, [transactions, data, formatTransactionsAsMarkers]);
  
  // Update chart when data changes significantly
  useEffect(() => {
    if (data.length > 0) {
      cleanUpChart();
      setForceUpdate(prev => prev + 1);
    }
  }, [data.length, plannedLevels.length]);
  
  // Initialize chart when container is available
  useEffect(() => {
    // Skip if container not ready or no data
    if (!chartContainerRef.current || data.length === 0) {
      return;
    }
    
    try {
      // Always clean up previous chart first
      cleanUpChart();
      
      // Format the data properly for the chart
      const formattedData = formatChartData(data);
      if (formattedData.length === 0) {
        setError("Invalid price data format");
        return;
      }
      
      // Store formatted data for use in event handlers
      chartContainerRef.current.dataset.formattedData = JSON.stringify(formattedData);
      
      // Create chart instance with enhanced visuals
      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: 400,
        layout: {
          background: { 
            type: 'solid', 
            color: CHART_THEME.background 
          },
          textColor: CHART_THEME.text,
          fontSize: 10,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
        },
        grid: {
          vertLines: {
            color: CHART_THEME.grid,
            style: 1,
          },
          horzLines: {
            color: CHART_THEME.grid,
            style: 1,
          },
        },
        crosshair: {
          mode: CrosshairMode.Normal,
          vertLine: {
            color: CHART_THEME.crosshair,
            width: 1,
            style: 0,
            labelBackgroundColor: CHART_THEME.background,
          },
          horzLine: {
            color: CHART_THEME.crosshair,
            width: 1,
            style: 0,
            labelBackgroundColor: CHART_THEME.background,
          }
        },
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          borderColor: CHART_THEME.border,
          textColor: CHART_THEME.text,
          rightOffset: 12,
        },
        rightPriceScale: {
          borderColor: CHART_THEME.border,
          textColor: CHART_THEME.text,
          scaleMargins: {
            top: 0.2,
            bottom: 0.2,
          },
        },
        leftPriceScale: {
          visible: false,
        },
        localization: {
          priceFormatter: price => {
            return formatChartPriceLabel(price, currentPortfolio?.currency);
          },
        },
        watermark: {
          visible: true,
          fontSize: 24,
          horzAlign: 'left',
          vertAlign: 'bottom',
          color: CHART_THEME.watermark,
          text: ticker,
        },
      });
      
      // Store chart reference
      chartRef.current = chart;
      chartCreatedRef.current = true;
      
      // Create initial series (will be updated with data later)
      const lineSeries = chart.addLineSeries({
        color: CHART_THEME.price.line,
        lineWidth: 2,
        lineType: 0,
        crosshairMarkerVisible: true,
        crosshairMarkerRadius: 4,
        lastValueVisible: true,
        priceLineVisible: true,
        priceLineSource: 'lastVisible',
        priceLineWidth: 1,
        priceLineColor: CHART_THEME.price.line,
        priceLineStyle: 2,
        // Add marker properties to ensure they're displayed
        markers: {
          visible: true,
          attachToSeries: true,
          shape: 'circle',
          size: 2
        }
      });
      
      const areaSeries = chart.addAreaSeries({
        topColor: CHART_THEME.price.area.top,
        bottomColor: CHART_THEME.price.area.bottom,
        lineColor: CHART_THEME.price.line,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false,
        visible: true,
      });
      
      // Risk score overlay line (0–100, own hidden scale)
      let riskScoreSeries = null;
      if (riskScoreHistory.length > 0) {
        riskScoreSeries = chart.addLineSeries({
          color: CHART_THEME.riskScore,
          lineWidth: 1,
          lineStyle: 1,
          priceScaleId: 'left',
          lastValueVisible: false,
          priceLineVisible: false,
          crosshairMarkerVisible: false,
          title: '',
        });

        riskScoreSeries.setData(getRiskScoreChartData(riskScoreHistory));
      }

      // Build risk-score lookup keyed by normalized timestamp (seconds)
      const riskScoreByTime = new Map();
      for (const p of riskScoreHistory) {
        riskScoreByTime.set(normalizeTimeValue(p.time), p.score);
      }

      // Store series references
      lineSeriesRef.current = lineSeries;
      areaSeriesRef.current = areaSeries;
      riskScoreSeriesRef.current = riskScoreSeries;
      
      // Create and style the tooltip HTML element
      const toolTip = document.createElement('div');
      toolTip.className = 'chart-tooltip';
      toolTip.style.cssText = `
        position: fixed;
        display: none;
        padding: 10px;
        box-sizing: border-box;
        font-size: 14px;
        line-height: 1.5;
        text-align: left;
        z-index: 9999;
        pointer-events: none;
        border-radius: 4px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);
        background-color: ${CHART_THEME.price.tooltip.background};
        color: ${CHART_THEME.price.tooltip.text};
        border: 2px solid ${CHART_THEME.price.tooltip.border};
        width: 150px;
        min-height: 80px;
      `;
      
      // Append tooltip to the document body
      document.body.appendChild(toolTip);
      toolTipRef.current = toolTip;
      
      // Store ticker in dataset for tooltip access
      chartContainerRef.current.dataset.ticker = ticker;
      
      // Subscribe to crosshairMove event to update tooltip
      chart.subscribeCrosshairMove(param => {
        if (!param.point) {
          toolTip.style.display = 'none';
          return;
        }
        
        // Get price data - with proper error handling
        let price = null;
        let dateStr = param.time;
        let foundData = false;
        
        try {
          // Try to get price from series data
          if (param.seriesData && (param.seriesData.has(lineSeries) || param.seriesData.has(areaSeries))) {
            let data;
            
            if (param.seriesData.has(lineSeries)) {
              data = param.seriesData.get(lineSeries);
            } else if (param.seriesData.has(areaSeries)) {
              data = param.seriesData.get(areaSeries);
            }
            
            if (data) {
              if (data.value !== undefined) {
                price = data.value;
                foundData = true;
              } else if (data.close !== undefined) {
                price = data.close;
                foundData = true;
              }
            }
          }
          
          // If we still don't have price data but have a valid time value
          if (!foundData && param.time) {
            try {
              // Get saved formatted data
              const savedData = chartContainerRef.current.dataset.formattedData;
              if (savedData) {
                const parsedData = JSON.parse(savedData);
                // Find the data point that matches the time
                const matchingPoint = parsedData.find(point => point.time === param.time);
                if (matchingPoint && matchingPoint.value !== undefined) {
                  price = matchingPoint.value;
                  foundData = true;
                }
              }
            } catch (err) {
              // Silent fail
            }
          }
        } catch (err) {
          // Silent fail
        }
        
        // Set tooltip content based on data availability
        if (!foundData) {
          toolTip.innerHTML = `
            <div style="font-weight: bold; margin-bottom: 4px; color: ${CHART_THEME.price.line};">${chartContainerRef.current.dataset.ticker || ticker}</div>
            <div style="font-size: 14px; margin-bottom: 4px;">Move to a data point</div>
          `;
        } else {
          // Format date if available
          let formattedDate = 'N/A';
          try {
            if (typeof dateStr === 'string' && dateStr.includes('-')) {
              // ISO date string format
              const date = new Date(dateStr);
              if (!isNaN(date.getTime())) {
                formattedDate = date.toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                });
              }
            } else if (dateStr) {
              // Unix timestamp or other numerical format
              const timestamp = typeof dateStr === 'number' ? dateStr : parseInt(dateStr);
              if (!isNaN(timestamp)) {
                // If timestamp is in seconds (common in chart libraries), convert to milliseconds
                const multiplier = timestamp > 2000000000 ? 1 : 1000;
                const date = new Date(timestamp * multiplier);
                if (!isNaN(date.getTime())) {
                  formattedDate = date.toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  });
                }
              }
            }
          } catch (e) {
            // Silent fail
          }
          
          // Get risk score value for this time point
          let riskScoreText = '';
          if (riskScoreByTime.size > 0 && param.time != null) {
            const timeKey = normalizeTimeValue(param.time);
            const rsVal = riskScoreByTime.get(timeKey);
            if (rsVal != null) {
              riskScoreText = `<div style="font-size: 12px; margin-top: 2px; color: ${getRiskScoreColor(rsVal)};">Risk: ${Math.round(rsVal)}/100</div>`;
            }
          }

          // Set tooltip content with the price data
          toolTip.innerHTML = `
            <div style="font-weight: bold; margin-bottom: 4px; color: ${CHART_THEME.price.line};">${chartContainerRef.current.dataset.ticker || ticker}</div>
            <div style="font-size: 16px; margin-bottom: 4px;">${formatPrice(price, currentPortfolio?.currency)}</div>
            ${riskScoreText}
            <div style="color: ${CHART_THEME.text};">${formattedDate}</div>
          `;
        }
        
        // Get chart container position and position tooltip
        const rect = chartContainerRef.current.getBoundingClientRect();
        const left = rect.left + param.point.x + 15;
        const top = rect.top + param.point.y - 50;
        
        // Apply positioning
        toolTip.style.left = `${left}px`;
        toolTip.style.top = `${top}px`;
        toolTip.style.display = 'block';
      });
      
      // Handle window resize
      const handleResize = () => {
        if (chartContainerRef.current && chart) {
          chart.applyOptions({
            width: chartContainerRef.current.clientWidth,
          });
        }
      };
      
      window.addEventListener('resize', handleResize);
      
      // Store the current planned levels to reference
      prevPlannedLevelsRef.current = plannedLevels;
      
      // Initial data setup
      updateChartData(formattedData, averageBuyPrice, transactions, plannedLevels);
      
      // Fit the chart content
      chart.timeScale().fitContent();
      
      // Return cleanup function
      return () => {
        window.removeEventListener('resize', handleResize);
        cleanUpChart();
      };
    } catch (err) {
      setError(`Chart creation error: ${err.message}`);
    }
  }, [
    data, 
    darkMode, 
    forceUpdate, 
    transactions, 
    averageBuyPrice,
    plannedLevels,
    ticker,
    riskScoreHistory,
    updateChartData,
    CHART_THEME.background,
    CHART_THEME.text,
    CHART_THEME.grid,
    CHART_THEME.crosshair,
    CHART_THEME.border,
    CHART_THEME.watermark,
    CHART_THEME.riskScore,
    CHART_THEME.price.line,
    CHART_THEME.price.area.top,
    CHART_THEME.price.area.bottom,
    CHART_THEME.price.tooltip.background,
    CHART_THEME.price.tooltip.text,
    CHART_THEME.price.tooltip.border,
    currentPortfolio?.currency
  ]);
  
  return (
    <div className="w-full border border-gray-200 dark:border-gray-700 rounded overflow-hidden">
      {error ? (
        <div className="flex justify-center items-center h-64 text-gray-400 dark:text-gray-400">
          {error}
        </div>
      ) : (
        <div 
          ref={chartContainerRef} 
          className="chart-container"
          style={{ height: '400px', width: '100%' }}
        />
      )}
    </div>
  );
};

export default PriceChart; 